import React, { useEffect, useMemo, useState } from "react";
import { api, fmtNumber } from "@/lib/api";
import { Card, Section } from "@/components/Primitives";
import KpiCard from "@/components/KpiCard";
import FilterBar from "@/components/FilterBar";
import ChartBlock from "@/components/ChartBlock";
import SearchSelect from "@/components/SearchSelect";
import TargetCompareChart from "@/components/TargetCompareChart";
import InstallAppPrompt from "@/components/InstallAppPrompt";
import {
  Coins, Receipt, Ticket, TrendUp, Warehouse, CheckCircle, WarningCircle,
  MagnifyingGlass, Target as TargetIcon, FunnelSimple, Sparkle, Package, Tag, Percent,
} from "@phosphor-icons/react";

const useQS = (obj) => useMemo(() => {
  const p = new URLSearchParams();
  Object.entries(obj || {}).forEach(([k, v]) => { if (v) p.append(k, v); });
  const s = p.toString();
  return s ? `?${s}` : "";
}, [obj]);

const Dashboard = () => {
  // ========== Global filters (APPLY to KPIs / Trend / Mix below) ==========
  const [filters, setFilters] = useState({ preset: "ytd" });
  const qs = useQS(filters);

  // ========== Target view — its own filters ==========
  const [targetYear, setTargetYear] = useState(2026);
  const [targetCompany, setTargetCompany] = useState("");
  const [targetQ, setTargetQ] = useState("");
  const [onlyBehind, setOnlyBehind] = useState(false);
  const [onlyOnTrack, setOnlyOnTrack] = useState(false);
  const [targetTier, setTargetTier] = useState(2); // 1/2/3
  const [targetRange, setTargetRange] = useState("ytd"); // "ytd" | "full" | "custom"
  const [targetStart, setTargetStart] = useState("");
  const [targetEnd, setTargetEnd] = useState("");
  const [companyOptions, setCompanyOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);

  // ========== YoY comparison — its own filters (Company/Brand/Location/Salesman + Dates) ==========
  const [yoyFilters, setYoyFilters] = useState({});
  const [yoyKind, setYoyKind] = useState("bar");
  // YoY-scoped KPIs so "Delta at a glance" reflects the local filters too
  // (without these, the numbers stayed global while only the chart moved).
  const [yoyKpi, setYoyKpi] = useState(null);

  // Chart kind toggle for the monthly trend block
  const [trendKind, setTrendKind] = useState("bar");

  // Donut: salesman contribution — filtered by location_code
  const [contribLoc, setContribLoc] = useState("");
  const [contribData, setContribData] = useState([]);
  const [contribPage, setContribPage] = useState(0);

  // Donut: BRAND contribution by location — filtered by location_code
  const [brandLoc, setBrandLoc] = useState("");
  const [brandLocData, setBrandLocData] = useState([]);
  const [brandLocPage, setBrandLocPage] = useState(0);

  // Donut: LOCATION contribution per brand — pick a brand, see its boutique mix
  const [locBrand, setLocBrand] = useState("");
  const [locBrandData, setLocBrandData] = useState([]);
  const [locBrandPage, setLocBrandPage] = useState(0);

  // Donut: SALESMAN contribution to a brand — pick a brand, see who sells it
  const [salesBrand, setSalesBrand] = useState("");
  const [salesBrandData, setSalesBrandData] = useState([]);
  const [salesBrandPage, setSalesBrandPage] = useState(0);

  // Brand options for the two new pickers above (separate from `brands` which
  // holds aggregated brand sales rows from `/analytics/brands`).
  const [brandRefList, setBrandRefList] = useState([]);
  const PAGE_SIZE = 12;

  const [kpi, setKpi] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [yoy, setYoY] = useState([]);
  const [brands, setBrands] = useState([]);
  const [locs, setLocs] = useState([]);
  const [targetData, setTargetData] = useState(null);
  const [seedState, setSeedState] = useState(null);

  // ========== Top matrix: 4 period comparisons (Last 7d, MTD, QTD, YTD) ==========
  const [periodKpis, setPeriodKpis] = useState({});
  // Data freshness — anchors all preset windows on the latest meaningful sale date
  const [dataInfo, setDataInfo] = useState(null);

  useEffect(() => {
    api.get("/system/data-info").then((r) => setDataInfo(r.data)).catch(() => {});
    const presets = ["last_7", "mtd", "qtd", "ytd"];
    presets.forEach((p) => {
      api.get(`/analytics/kpis?preset=${p}`).then((r) => {
        setPeriodKpis((prev) => ({ ...prev, [p]: r.data }));
      }).catch(() => {});
    });
  }, []);

  useEffect(() => {
    api.get("/ref/companies").then((r) => setCompanyOptions(r.data.rows || []));
    api.get("/ref/locations").then((r) => setLocationOptions(r.data.rows || []));
    api.get("/ref/brands").then((r) => setBrandRefList(r.data.rows || []));
  }, []);

  useEffect(() => {
    let stop = false;
    const tick = async () => {
      const { data } = await api.get("/system/seed-status");
      if (!stop) {
        setSeedState(data);
        if (["seeding", "loading_excel", "indexing"].includes(data.status)) setTimeout(tick, 4000);
      }
    };
    tick();
    return () => { stop = true; };
  }, []);

  useEffect(() => {
    api.get(`/analytics/kpis${qs}`).then((r) => setKpi(r.data));
    api.get(`/analytics/trend/monthly${qs}`).then((r) => setMonthly(r.data.rows || []));
    api.get(`/analytics/brands${qs}`).then((r) => setBrands(r.data.rows || []));
    api.get(`/analytics/locations${qs}`).then((r) => setLocs(r.data.rows || []));
  }, [qs]);

  // Target data — refetches when targetYear, targetBrand, or targetCompany changes
  useEffect(() => {
    const p = new URLSearchParams();
    p.append("year", targetYear);
    p.append("tier", targetTier);
    p.append("range_kind", targetRange);
    if (targetRange === "custom") {
      if (targetStart) p.append("start_date", targetStart);
      if (targetEnd) p.append("end_date", targetEnd);
    }
    if (targetCompany) p.append("company", targetCompany);
    if (targetQ) p.append("q", targetQ);
    if (onlyBehind) p.append("only_behind", "true");
    if (onlyOnTrack) p.append("only_on_track", "true");
    api.get(`/analytics/target-progress?${p.toString()}`).then((r) => setTargetData(r.data));
  }, [targetYear, targetCompany, targetQ, onlyBehind, onlyOnTrack, targetTier, targetRange, targetStart, targetEnd]);

  // Salesman contribution fetch (donut) — reacts to the location picker.
  useEffect(() => {
    if (!contribLoc) { setContribData([]); return; }
    const p = new URLSearchParams();
    p.append("location_code", contribLoc);
    p.append("preset", "ytd");
    api.get(`/analytics/salesman-by-location?${p.toString()}&limit=500`)
      .then((r) => { setContribData(r.data.rows || []); setContribPage(0); });
  }, [contribLoc]);

  // Brand contribution by location — uses the existing /analytics/brands
  // endpoint scoped to a single location. Computes contribution_pct client-side
  // so the donut + list mirror the salesman section exactly.
  useEffect(() => {
    if (!brandLoc) { setBrandLocData([]); return; }
    const p = new URLSearchParams();
    p.append("location_code", brandLoc);
    p.append("preset", "ytd");
    api.get(`/analytics/brands?${p.toString()}`).then((r) => {
      const rows = r.data.rows || [];
      const total = rows.reduce((a, b) => a + (b.net_value || 0), 0) || 1;
      setBrandLocData(rows.map((b) => ({
        ...b,
        contribution_pct: total ? (b.net_value / total) * 100 : 0,
      })));
      setBrandLocPage(0);
    });
  }, [brandLoc]);

  // Location contribution per brand — pick a brand, see boutique mix.
  useEffect(() => {
    if (!locBrand) { setLocBrandData([]); return; }
    const p = new URLSearchParams();
    p.append("brand", locBrand);
    p.append("preset", "ytd");
    api.get(`/analytics/locations-by-brand?${p.toString()}&limit=500`)
      .then((r) => { setLocBrandData(r.data.rows || []); setLocBrandPage(0); });
  }, [locBrand]);

  // Salesman contribution to a brand — pick a brand, see top sellers across the house.
  useEffect(() => {
    if (!salesBrand) { setSalesBrandData([]); return; }
    const p = new URLSearchParams();
    p.append("brand", salesBrand);
    p.append("preset", "ytd");
    api.get(`/analytics/salesmen-by-brand?${p.toString()}&limit=500`)
      .then((r) => { setSalesBrandData(r.data.rows || []); setSalesBrandPage(0); });
  }, [salesBrand]);

  // YoY — refetches whenever any yoyFilter changes (chart + Delta-at-a-glance KPIs)
  useEffect(() => {
    const p = new URLSearchParams();
    Object.entries(yoyFilters || {}).forEach(([k, v]) => { if (v) p.append(k, v); });
    const q = p.toString();
    api.get(`/analytics/yoy${q ? `?${q}` : ""}`).then((r) => setYoY(r.data.rows || []));
    // Same filters applied to the KPI endpoint (preset=ytd) so the "Delta at
    // a glance" card stays in lock-step with the chart below it.
    const pk = new URLSearchParams(p);
    pk.set("preset", "ytd");
    api.get(`/analytics/kpis?${pk.toString()}`).then((r) => setYoyKpi(r.data)).catch(() => setYoyKpi(null));
  }, [yoyFilters]);

  const yoyYears = Array.from(new Set(yoy.flatMap((r) => Object.keys(r).filter((k) => k !== "month")))).sort();
  const currYear = yoyYears[yoyYears.length - 1];
  const prevYear = yoyYears.length > 1 ? yoyYears[yoyYears.length - 2] : null;
  // YTD-aligned comparison: only count the months that the CURRENT year has
  // actually reported (value > 0). For the prior year we sum the **same** set
  // of months so we compare like-for-like (e.g. Jan–Apr 2025 vs Jan–Apr 2026).
  const ytdMonths = currYear
    ? yoy.filter((r) => (r[currYear] || 0) > 0).map((r) => r.month)
    : [];
  const sumYTD = (y) =>
    ytdMonths.length
      ? yoy.filter((r) => ytdMonths.includes(r.month)).reduce((a, r) => a + (r[y] || 0), 0)
      : yoy.reduce((a, r) => a + (r[y] || 0), 0);
  const currSum = sumYTD(currYear);
  const prevSum = sumYTD(prevYear);
  const deltaPct = prevSum ? ((currSum - prevSum) / prevSum) * 100 : null;
  const ytdLabel = ytdMonths.length ? `${ytdMonths[0]} → ${ytdMonths[ytdMonths.length - 1]}` : "Full year";

  // Day-precise YTD totals from the same `/analytics/kpis` source the matrix
  // uses, so the "Delta at a glance" card and the matrix's YTD card never
  // disagree. When any YoY-local filter is set, we switch to the scoped
  // `yoyKpi` so the numbers obey the filters in the comparison section.
  const yoyScoped = yoyKpi && Object.values(yoyFilters || {}).some(Boolean);
  const dpCurrSum = (yoyScoped ? yoyKpi.total_sales : periodKpis.ytd?.total_sales) ?? null;
  const dpPrevSum = (yoyScoped ? yoyKpi.prior_year_sales : periodKpis.ytd?.prior_year_sales) ?? null;
  const dpDeltaPct = (dpPrevSum && dpCurrSum != null)
    ? ((dpCurrSum - dpPrevSum) / dpPrevSum) * 100
    : null;
  // YTD-aligned dataset for the monthly trajectory chart — only show months the
  // current year has actually reported so the previous-year line truncates at
  // the same window (no flat-to-zero tail for the in-progress year).
  // Map "01" → "Jan" for a friendlier X-axis label while keeping the YoY
  // filter comparison intact.
  const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const yoyYtd = (ytdMonths.length ? yoy.filter((r) => ytdMonths.includes(r.month)) : yoy)
    .map((r) => ({ ...r, month: MONTH_SHORT[parseInt(r.month, 10) - 1] || r.month }));

  const targetRows = targetData?.rows || [];
  const onTrackCount = targetRows.filter((r) => r.on_track).length;
  const behindCount = targetRows.filter((r) => r.achievement_pct != null && !r.on_track).length;

  const resetTargetFilters = () => { setTargetCompany(""); setTargetQ(""); setOnlyBehind(false); setOnlyOnTrack(false); };

  const targetPngUrl = useMemo(() => {
    const p = new URLSearchParams();
    p.append("kind", "target"); p.append("year", targetYear);
    if (targetCompany) p.append("company", targetCompany);
    return `${process.env.REACT_APP_BACKEND_URL}/api/export/chart-png?${p.toString()}`;
  }, [targetYear, targetCompany]);

  return (
    <div>
      {/* Lightweight top bar — print action only; the global header now carries
          page identity + the "data as of" badge, so the giant Pulse-of-the-House
          masthead has been retired to keep the dashboard scan-friendly. */}
      <div className="flex items-center justify-between mb-5">
        <div className="text-[10px] uppercase tracking-[0.3em] text-myb-500 font-semibold">Morad Yousuf Behbehani · Trading Division</div>
        <button
          onClick={() => window.print()}
          data-testid="dashboard-print-btn"
          className="no-print text-xs uppercase tracking-[0.25em] border border-myb-200 hover:border-myb-500 hover:text-myb-500 rounded-sm px-4 py-2 transition-all font-semibold text-muted-foreground"
        >
          Print View
        </button>
      </div>

      {/* Snapshot — a friendly executive one-liner */}
      <div data-testid="dashboard-snapshot" className="mb-6 bg-myb-500 text-white rounded-sm px-5 py-4 shadow-[0_4px_20px_rgba(13,56,128,0.15)] flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
          <div>
            <div className="text-[9px] uppercase tracking-[0.3em] text-myb-100 font-semibold">Snapshot</div>
            <div className="text-sm mt-0.5">
              {targetData ? (
                <>Day <span className="font-semibold">{targetData.day_of_year}</span> of 365 · <span className="font-semibold">{targetData.expected_pct}%</span> through the year</>
              ) : "Loading today's pulse…"}
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.3em] text-myb-100 font-semibold">YTD · {targetYear}</div>
            <div className="text-sm mt-0.5 font-medium">{periodKpis.ytd ? `${fmtNumber(periodKpis.ytd.total_sales)} KWD` : "—"}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.3em] text-myb-100 font-semibold">Location status</div>
            <div className="text-sm mt-0.5"><span className="font-semibold text-emerald-200">{onTrackCount} on track</span> · <span className="font-semibold text-rose-200">{behindCount} behind</span></div>
          </div>
          {(dpDeltaPct ?? deltaPct) != null && (
            <div>
              <div className="text-[9px] uppercase tracking-[0.3em] text-myb-100 font-semibold">YoY</div>
              <div className="text-sm mt-0.5 font-medium">{(dpDeltaPct ?? deltaPct) >= 0 ? "▲" : "▼"} {Math.abs(dpDeltaPct ?? deltaPct).toFixed(1)}% vs {prevYear || "LY"}</div>
            </div>
          )}
        </div>
        <a
          href="/chat"
          className="text-[10px] uppercase tracking-[0.3em] bg-white/10 hover:bg-white/20 border border-white/30 rounded-full px-4 py-2 font-semibold inline-flex items-center gap-2"
        >
          <Sparkle weight="fill" size={12} /> Ask Analyst for deeper insight
        </a>
      </div>

      {seedState && seedState.status !== "ready" && seedState.status !== "already_seeded" && (
        <div data-testid="seed-banner" className="mb-6 bg-myb-50 border border-myb-200 rounded-sm px-5 py-3 text-sm flex items-center gap-4">
          <div className="h-2 w-2 rounded-full bg-myb-500 animate-pulse" />
          <div>
            <div className="text-myb-600 font-medium">Seeding master dataset …</div>
            <div className="text-xs text-muted-foreground">{seedState.message} {seedState.total ? `(${fmtNumber(seedState.progress)} / ${fmtNumber(seedState.total)})` : ""}</div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* PERIOD COMPARISON MATRIX (4 windows · this year vs last year)            */}
      {/* ========================================================================= */}
      <section data-testid="period-matrix" className="mb-10">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-myb-500 font-semibold">Period comparisons</div>
            <h2 className="font-display text-2xl lg:text-3xl tracking-tight font-light text-foreground mt-1">This year vs last year — at every cadence</h2>
            {dataInfo?.latest_data_date && (
              <div className="text-[11px] text-muted-foreground mt-1">
                Windows anchored on the last day of real sales —{" "}
                <span className="font-semibold text-myb-500">
                  {new Date(dataInfo.latest_data_date).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" })}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { k: "last_7", label: "Last 7 Days" },
            { k: "mtd", label: "Month To Date" },
            { k: "qtd", label: "Quarter To Date" },
            { k: "ytd", label: "Year To Date" },
          ].map(({ k, label }) => {
            const d = periodKpis[k];
            const cy = d?.total_sales || 0;
            const ly = d?.prior_year_sales || 0;
            const max = Math.max(cy, ly, 1);
            const delta = d?.growth_pct;
            const positive = delta != null && delta >= 0;
            return (
              <div
                key={k}
                data-testid={`period-card-${k}`}
                className="rounded-sm border border-myb-100 bg-white p-5 hover:border-myb-300 transition-all shadow-[0_1px_8px_rgba(13,56,128,0.04)]"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-myb-500 font-semibold">{label}</div>
                  {delta != null && (
                    <span className={`text-[10px] uppercase tracking-wider font-semibold inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${
                      positive ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"
                    }`}>
                      {positive ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}%
                    </span>
                  )}
                </div>
                {/* CY bar */}
                <div className="space-y-2.5">
                  <div>
                    <div className="flex items-baseline justify-between">
                      <span className="text-[10px] uppercase tracking-[0.2em] text-myb-500/80 font-semibold">This year</span>
                      <span className="font-display text-xl text-myb-500 tabular-nums">{fmtNumber(cy)}</span>
                    </div>
                    <div className="h-1.5 bg-myb-50 rounded-full overflow-hidden mt-1">
                      <div className="h-full bg-myb-500" style={{ width: `${(cy / max) * 100}%` }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-baseline justify-between">
                      <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Last year</span>
                      <span className="text-base text-muted-foreground tabular-nums">{fmtNumber(ly)}</span>
                    </div>
                    <div className="h-1.5 bg-myb-50 rounded-full overflow-hidden mt-1">
                      <div className="h-full bg-amber-400/80" style={{ width: `${(ly / max) * 100}%` }} />
                    </div>
                  </div>
                </div>
                <div className="text-[10px] text-muted-foreground/70 mt-3 font-mono tabular-nums">KWD net sales · same window</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ========================================================================= */}
      {/* SECTION A — TARGET VIEW (self-contained, own filters)                     */}
      {/* ========================================================================= */}
      <section
        data-testid="target-section"
        className="relative rounded-sm border border-myb-200 bg-myb-50/30 p-5 md:p-7 mb-12 shadow-[0_2px_18px_rgba(13,56,128,0.06)]"
      >
        <div className="absolute -top-2.5 left-5 md:left-7 bg-myb-500 text-white text-[9px] uppercase tracking-[0.3em] font-semibold px-2.5 py-1 rounded-sm inline-flex items-center gap-1.5">
          <TargetIcon size={10} weight="bold" /> Target View · scoped to this block only
        </div>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-5 mt-1">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-myb-500 mb-1 font-semibold">Year target</div>
            <h2 className="font-display text-2xl lg:text-3xl tracking-tight font-light text-foreground">Are we on track?</h2>
          </div>
          <div className="flex items-center gap-3 flex-wrap no-print" data-testid="target-year-toggle">
            <span className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">Tier</span>
            <div className="flex border hairline bg-white rounded-sm overflow-hidden">
              {[
                { t: 1, label: "Target 1", hint: "Target-1" },
                { t: 2, label: "Target 2", hint: "Target-2" },
                { t: 3, label: "Target 3", hint: "Target-3" },
              ].map(({ t, label, hint }) => (
                <button
                  key={t}
                  data-testid={`target-tier-${t}`}
                  onClick={() => setTargetTier(t)}
                  title={hint}
                  className={`text-[11px] px-3 py-1.5 uppercase tracking-wider font-semibold transition-all ${
                    targetTier === t ? "bg-myb-500 text-white" : "text-muted-foreground hover:text-myb-500 hover:bg-myb-50"
                  }`}
                >{label}</button>
              ))}
            </div>

            <span className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">Year</span>
            <div className="flex border hairline bg-white rounded-sm overflow-hidden">
              {[2025, 2026].map((y) => (
                <button
                  key={y}
                  data-testid={`target-year-${y}`}
                  onClick={() => setTargetYear(y)}
                  className={`text-xs px-3 py-1.5 uppercase tracking-wider font-semibold transition-all ${
                    targetYear === y ? "bg-myb-500 text-white" : "text-muted-foreground hover:text-myb-500 hover:bg-myb-50"
                  }`}
                >{y}</button>
              ))}
            </div>

            <span className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">Range</span>
            <div className="flex border hairline bg-white rounded-sm overflow-hidden">
              {[
                { k: "last_7", label: "Last 7d" },
                { k: "mtd", label: "MTD" },
                { k: "qtd", label: "QTD" },
                { k: "ytd", label: "YTD" },
                { k: "full", label: "Full yr" },
                { k: "custom", label: "Custom" },
              ].map(({ k, label }) => (
                <button
                  key={k}
                  data-testid={`target-range-${k}`}
                  onClick={() => setTargetRange(k)}
                  className={`text-[11px] px-2.5 py-1.5 uppercase tracking-wider font-semibold transition-all ${
                    targetRange === k ? "bg-myb-500 text-white" : "text-muted-foreground hover:text-myb-500 hover:bg-myb-50"
                  }`}
                >{label}</button>
              ))}
            </div>
            {targetRange === "custom" && (
              <div className="flex items-center gap-1.5">
                <input type="date" data-testid="target-start" value={targetStart}
                       onChange={(e) => setTargetStart(e.target.value)}
                       className="bg-white border border-border focus:border-myb-500 rounded-sm px-2 py-1.5 text-xs outline-none" />
                <span className="text-muted-foreground text-xs">→</span>
                <input type="date" data-testid="target-end" value={targetEnd}
                       onChange={(e) => setTargetEnd(e.target.value)}
                       className="bg-white border border-border focus:border-myb-500 rounded-sm px-2 py-1.5 text-xs outline-none" />
              </div>
            )}
            <a
              href={targetPngUrl}
              target="_blank" rel="noreferrer"
              data-testid="target-png-btn"
              className="text-xs uppercase tracking-[0.22em] border border-myb-200 hover:border-myb-500 hover:text-myb-500 rounded-sm px-3 py-1.5 text-muted-foreground font-semibold bg-white"
            >Download PNG</a>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-5 mb-5">
          <Card title={`${({last_7:"Last 7 days", mtd:"MTD", qtd:"QTD", ytd:"YTD", full:"Full-year", custom:"Period"}[targetRange] || "Period")} sales · ${targetYear}`} testId="target-total">
            <div className="font-display text-4xl font-light text-myb-500 leading-none">{fmtNumber(targetRows.reduce((a, r) => a + r.ytd_net_value, 0))}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1 font-semibold">
              KWD
              {targetData?.window ? <span className="block mt-0.5 normal-case tracking-normal text-[10px] text-muted-foreground/80">{targetData.window.start} → {targetData.window.end}</span> : null}
            </div>
          </Card>
          <Card title="Expected pace">
            <div className="font-display text-4xl font-light text-foreground leading-none">{targetData ? `${targetData.expected_pct}%` : "—"}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1 font-semibold">Day {targetData?.day_of_year || "—"} / 365</div>
          </Card>
          <Card title="Locations on track" testId="target-on-track">
            <div className="font-display text-4xl font-light text-emerald-600 leading-none flex items-center gap-2"><CheckCircle weight="fill" size={24} /> {onTrackCount}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1 font-semibold">At or above expected pace</div>
          </Card>
          <Card title="Locations behind" testId="target-behind">
            <div className="font-display text-4xl font-light text-rose-500 leading-none flex items-center gap-2"><WarningCircle weight="fill" size={24} /> {behindCount}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1 font-semibold">Below 90% of expected</div>
          </Card>
        </div>

        {/* ── NEW filter row (above the table, scoped to target only) ── */}
        <div
          data-testid="target-table-filter"
          className="flex flex-wrap items-center gap-3 bg-white border hairline rounded-sm px-4 py-3 mb-3 no-print"
        >
          <div className="inline-flex items-center gap-2">
            <FunnelSimple size={14} className="text-myb-500" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-myb-500 font-semibold">Target filters</span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Company</span>
            <SearchSelect
              testId="target-company-filter"
              width="w-[200px]"
              height="h-8"
              placeholder="All companies"
              allLabel="All companies"
              value={targetCompany}
              onChange={(v) => setTargetCompany(v)}
              options={companyOptions.map((c) => ({ value: String(c.code), label: c.name, sub: c.code }))}
            />
          </div>

          <div className="relative flex-1 max-w-xs">
            <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-myb-500 pointer-events-none" />
            <input
              data-testid="target-search"
              type="text"
              value={targetQ}
              onChange={(e) => setTargetQ(e.target.value)}
              placeholder="Search locations…"
              className="w-full bg-white border border-border focus:border-myb-500 rounded-sm pl-7 pr-3 py-1.5 text-xs outline-none"
            />
          </div>

          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              data-testid="target-only-behind"
              type="checkbox"
              checked={onlyBehind}
              onChange={(e) => { setOnlyBehind(e.target.checked); if (e.target.checked) setOnlyOnTrack(false); }}
              className="accent-myb-500 w-3.5 h-3.5"
            />
            <span className="text-xs text-muted-foreground font-medium">Only behind target</span>
          </label>

          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              data-testid="target-only-on-track"
              type="checkbox"
              checked={onlyOnTrack}
              onChange={(e) => { setOnlyOnTrack(e.target.checked); if (e.target.checked) setOnlyBehind(false); }}
              className="accent-myb-500 w-3.5 h-3.5"
            />
            <span className="text-xs text-muted-foreground font-medium">Only on track</span>
          </label>

          {(targetCompany || targetQ || onlyBehind || onlyOnTrack) && (
            <button
              data-testid="target-filter-reset"
              onClick={resetTargetFilters}
              className="ml-auto text-[11px] uppercase tracking-wider text-muted-foreground hover:text-myb-500 font-semibold"
            >Reset</button>
          )}
        </div>

        {/* Progress table */}
        <Card testId="target-table" title={`Location progress · ${targetYear} (${targetRows.length} locations)`} className="!bg-white">
          {targetRows.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">No locations match these filters.</div>
          ) : (
            <div className="overflow-auto scrollbar-thin max-h-[500px]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white/95 backdrop-blur-sm">
                  <tr className="text-left">
                  {["Location", "YTD Sales (KWD)", `Prorated Target (KWD)`, `Annual Target ${targetYear} (KWD)`, "Achievement (prorated)", "Annual %", "On track"].map((h) => (
                    <th key={h} className="py-2 px-3 text-[10px] uppercase tracking-[0.2em] text-myb-500 font-semibold border-b hairline">{h}</th>
                  ))}
                  </tr>
                </thead>
                <tbody>
                  {targetRows.map((r) => (
                    <tr key={r.location_code} className="border-b hairline hover:bg-myb-50/50" data-testid={`target-row-${r.location_code}`}>
                      <td className="py-2.5 px-3">
                        <div>{r.location_name} <span className="text-muted-foreground font-mono text-[10px] ml-1">{r.location_code}</span></div>
                        {r.company_name && (
                          <div className="text-[10px] uppercase tracking-wider text-myb-500/80 font-semibold mt-0.5">{r.company_name}</div>
                        )}
                        {(r.target_1 || r.target_2 || r.target_3) ? (
                          <div className="text-[10px] text-muted-foreground mt-0.5 flex gap-2 font-mono tabular-nums" title="Target 1 · Target 2 · Target 3 achievement %">
                            <span className={r.achievement_pct_t1 != null && r.achievement_pct_t1 >= 90 ? "text-emerald-700" : r.achievement_pct_t1 != null ? "text-rose-600" : ""}>
                              T1 {r.achievement_pct_t1 != null ? `${r.achievement_pct_t1.toFixed(0)}%` : "—"}
                            </span>
                            <span className={r.achievement_pct_t2 != null && r.achievement_pct_t2 >= 90 ? "text-emerald-700" : r.achievement_pct_t2 != null ? "text-rose-600" : ""}>
                              T2 {r.achievement_pct_t2 != null ? `${r.achievement_pct_t2.toFixed(0)}%` : "—"}
                            </span>
                            <span className={r.achievement_pct_t3 != null && r.achievement_pct_t3 >= 90 ? "text-emerald-700" : r.achievement_pct_t3 != null ? "text-rose-600" : ""}>
                              T3 {r.achievement_pct_t3 != null ? `${r.achievement_pct_t3.toFixed(0)}%` : "—"}
                            </span>
                          </div>
                        ) : null}
                      </td>
                      <td className="py-2.5 px-3 font-medium">{fmtNumber(r.ytd_net_value)}</td>
                      <td className="py-2.5 px-3 font-medium text-foreground tabular-nums">
                        {r.target_amount ? fmtNumber(r.target_amount) : "—"}
                        {r.target_amount && !targetData?.window?.is_full_year && targetData?.window?.days ? (
                          <div className="text-[10px] text-muted-foreground/70 mt-0.5 font-mono tabular-nums">
                            {targetData.window.days} days · {((targetData.target_scale || 0) * 100).toFixed(1)}% of year
                          </div>
                        ) : (
                          <div className="text-[10px] text-muted-foreground/70 mt-0.5 font-mono tabular-nums">full year</div>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-muted-foreground tabular-nums">
                        {r[`target_${targetTier}_full`] ? fmtNumber(r[`target_${targetTier}_full`]) : "—"}
                        <div className="text-[10px] text-muted-foreground/60 mt-0.5 font-mono">tier {targetTier} · full year</div>
                      </td>
                      <td className="py-2.5 px-3">
                        {r.achievement_pct == null ? (
                          <span className="text-xs text-muted-foreground italic">Target not set</span>
                        ) : (
                          <div className="flex items-center gap-2 max-w-[220px]">
                            <div className="flex-1 h-1.5 bg-myb-50 rounded-full overflow-hidden">
                              <div
                                className={`h-full ${r.on_track ? "bg-emerald-500" : "bg-rose-500"}`}
                                style={{ width: `${Math.min(100, r.achievement_pct || 0)}%` }}
                              />
                            </div>
                            <span className="text-xs tabular-nums font-medium whitespace-nowrap">{`${r.achievement_pct.toFixed(0)}%`}</span>
                          </div>
                        )}
                      </td>
                      <td className="py-2.5 px-3">
                        {(() => {
                          const full = r[`target_${targetTier}_full`] || 0;
                          if (!full) return <span className="text-xs text-muted-foreground italic">—</span>;
                          const annualPct = (r.ytd_net_value / full) * 100;
                          return (
                            <div className="flex items-center gap-2 max-w-[180px]" title={`${fmtNumber(r.ytd_net_value)} / ${fmtNumber(full)}`}>
                              <div className="flex-1 h-1.5 bg-myb-50 rounded-full overflow-hidden">
                                <div
                                  className={`h-full ${annualPct >= 100 ? "bg-emerald-600" : annualPct >= 90 ? "bg-emerald-500" : annualPct >= 50 ? "bg-amber-500" : "bg-rose-400"}`}
                                  style={{ width: `${Math.min(100, annualPct)}%` }}
                                />
                              </div>
                              <span className={`text-xs tabular-nums font-medium whitespace-nowrap ${annualPct >= 100 ? "text-emerald-700" : annualPct >= 50 ? "text-foreground" : "text-muted-foreground"}`}>
                                {`${annualPct.toFixed(1)}%`}
                              </span>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="py-2.5 px-3">
                        {r.achievement_pct == null ? "—" :
                          r.on_track ? <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5 uppercase tracking-wider">on track</span>
                                      : <span className="text-[10px] bg-rose-50 text-rose-700 border border-rose-200 rounded-full px-2 py-0.5 uppercase tracking-wider">behind</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Target vs Sales comparison chart — self-contained with its own
            date range filter. Auto-shows ALL targeted locations. Sits
            directly below the table for at-a-glance visualisation. */}
        <Card testId="target-compare-card" title="Target vs Sales · compare locations" className="mt-6 !bg-white">
          <TargetCompareChart year={targetYear} tier={targetTier} />
        </Card>
      </section>

      {/* ========================================================================= */}
      {/* SECTION B — REST OF DASHBOARD                                              */}
      {/* ========================================================================= */}

      {/* YoY comparison */}
      <Section eyebrow={prevYear && currYear ? `${prevYear} vs ${currYear}` : "Year-over-year"} title="The comparison">
        {/* YoY-local dependent filters (Company / Brand / Location / Salesman + Dates) */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2" data-testid="yoy-filters">
          <div className="text-[10px] uppercase tracking-[0.28em] text-myb-500 font-semibold">Scope this comparison</div>
          <div className="flex border hairline bg-white rounded-sm overflow-hidden" data-testid="yoy-kind-toggle">
            {[
              { k: "line", label: "Line" },
              { k: "bar", label: "Bar" },
            ].map(({ k, label }) => (
              <button
                key={k}
                data-testid={`yoy-kind-${k}`}
                onClick={() => setYoyKind(k)}
                className={`text-[11px] px-3 py-1.5 uppercase tracking-wider font-semibold transition-all ${
                  yoyKind === k ? "bg-myb-500 text-white" : "text-muted-foreground hover:text-myb-500 hover:bg-myb-50"
                }`}
              >{label}</button>
            ))}
          </div>
        </div>
        <FilterBar value={yoyFilters} onChange={setYoyFilters} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card testId="yoy-summary" title="Delta at a glance" className="lg:col-span-1">
            <div className="space-y-5 mt-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">{currYear || "Current year"} · YTD</div>
                <div className="font-display text-4xl font-light text-myb-500 tracking-tight leading-none mt-1">{fmtNumber(dpCurrSum ?? currSum)}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1 font-semibold">KWD net sales</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">{prevYear || "Prior year"} · same window</div>
                <div className="font-display text-3xl font-light text-foreground tracking-tight leading-none mt-1">{fmtNumber(dpPrevSum ?? prevSum)}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1 font-semibold">KWD net sales · day-precise</div>
              </div>
              {(dpDeltaPct ?? deltaPct) != null && (
                <div className="flex items-center gap-2 pt-3 border-t hairline">
                  <div className={`inline-flex items-center gap-1 text-lg font-medium ${(dpDeltaPct ?? deltaPct) >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                    {(dpDeltaPct ?? deltaPct) >= 0 ? "▲" : "▼"} {Math.abs(dpDeltaPct ?? deltaPct).toFixed(1)}%
                  </div>
                  <div className="text-xs text-muted-foreground">YTD · {ytdLabel}</div>
                </div>
              )}
            </div>
          </Card>
          <Card testId="yoy-chart" title={`Monthly trajectory · ${prevYear || ""} vs ${currYear || ""} · YTD`} className="lg:col-span-2">
            <ChartBlock
              spec={{ kind: yoyKind, x_key: "month", y_keys: yoyYears }}
              data={yoyYtd}
              height={320}
            />
          </Card>
        </div>
      </Section>

      {/* KPI block — now under a proper Section header for visual parity
          with Mix / Team / YoY blocks. The scope row below the title keeps
          the filters discoverable but no longer substitutes for a heading. */}
      <Section
        eyebrow="Performance"
        title="Numbers at a glance"
        right={
          kpi?.prior_year_sales != null ? (
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-medium">
              vs prior year · <span className="tabular-nums text-myb-500 font-semibold">{fmtNumber(kpi.prior_year_sales)} KWD</span>
            </span>
          ) : null
        }
      >
        <div data-testid="kpi-section">
        <div className="mb-3 flex items-center gap-2">
          <FunnelSimple size={14} className="text-myb-500" />
          <span className="text-[10px] uppercase tracking-[0.28em] text-myb-500 font-semibold">Scope · company, location, brand, salesman &amp; dates</span>
        </div>
        <FilterBar value={filters} onChange={setFilters} />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5 mt-4">
          <KpiCard testId="kpi-total-sales" label="Total Sales" value={fmtNumber(kpi?.total_sales)} suffix="KWD" delta={kpi?.growth_pct} icon={Coins} />
          <KpiCard testId="kpi-transactions" label="Transactions" value={fmtNumber(kpi?.transactions)} delta={kpi?.txn_growth_pct} icon={Receipt} />
          <KpiCard testId="kpi-total-qty" label="Total Sale Qty" value={fmtNumber(kpi?.quantity)} delta={kpi?.qty_growth_pct} icon={Package} />
          <KpiCard testId="kpi-avg-ticket" label="Avg Ticket" value={fmtNumber(kpi?.avg_ticket)} suffix="KWD" icon={Ticket} />
          <KpiCard testId="kpi-avg-qty" label="Avg Qty / Bill" value={fmtNumber(kpi?.avg_qty)} icon={Package} />
          <KpiCard testId="kpi-gross-profit" label="Gross Profit" value={fmtNumber(kpi?.gross_profit)} suffix="KWD" icon={TrendUp} />
          <KpiCard testId="kpi-gp-margin" label="GP Margin" value={(kpi?.gp_margin_pct ?? 0).toFixed(1)} suffix="%" icon={Warehouse} />
          <KpiCard testId="kpi-discount" label="Discount" value={fmtNumber(kpi?.discount)} suffix="KWD" icon={Tag} />
          <KpiCard testId="kpi-discount-pct" label="Discount %" value={(kpi?.discount_pct ?? 0).toFixed(1)} suffix="of gross" icon={Percent} />
        </div>
        </div>
      </Section>

      {/* Mix — overview cards under a proper Section header. The redundant
          "Monthly sales movement" chart was retired because the YoY
          comparison chart above already plots monthly trajectories. */}
      <Section
        eyebrow="Mix"
        title="What's selling where"
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card testId="chart-brands" title="Brand Mix · top 6" className="lg:col-span-1">
            <ChartBlock spec={{ kind: "donut", x_key: "brand", y_keys: ["net_value"] }} data={brands.slice(0, 6)} height={300} />
          </Card>
          <Card testId="chart-top-locations" title="Top Boutiques · top 10" className="lg:col-span-2">
            <ChartBlock spec={{ kind: "bar", x_key: "location_name", y_keys: ["net_value"] }} data={locs.slice(0, 10)} height={300} />
          </Card>
        </div>
      </Section>

      {/* Salesman contribution donut */}
      <Section
        eyebrow="Team"
        title="Salesman contribution by boutique"
        right={
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Boutique</span>
            <SearchSelect
              testId="contrib-location-filter"
              width="w-[260px]"
              height="h-8"
              placeholder="Choose a location…"
              allLabel="— None —"
              value={contribLoc}
              onChange={(v) => setContribLoc(v)}
              options={locationOptions.map((l) => ({ value: String(l.code), label: l.name || l.code, sub: l.code }))}
            />
          </div>
        }
      >
        <Card testId="salesman-contribution">
          {!contribLoc ? (
            <div className="py-10 text-center text-sm text-muted-foreground italic">Pick a boutique above to see which salesman drives what share of its YTD net sales.</div>
          ) : contribData.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground italic">No transactions for this boutique in the YTD window.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
              <div className="md:col-span-2">
                <ChartBlock
                  spec={{ kind: "donut", x_key: "salesman_name", y_keys: ["net_value"] }}
                  data={contribData}
                  height={320}
                />
              </div>
              <div className="text-xs space-y-1.5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-myb-500 font-semibold">YTD contribution · {contribData.length} salesmen</div>
                </div>
                <div className="grid grid-cols-[minmax(0,1fr)_90px_56px] items-center gap-3 pb-1 border-b hairline text-[9px] uppercase tracking-wider text-muted-foreground/70 font-semibold">
                  <span>Salesman</span>
                  <span className="text-right">KWD</span>
                  <span className="text-right">Share</span>
                </div>
                {contribData
                  .slice(contribPage * PAGE_SIZE, (contribPage + 1) * PAGE_SIZE)
                  .map((r, i) => (
                  <div
                    key={r.salesman_code || i}
                    className="grid grid-cols-[minmax(0,1fr)_90px_56px] items-center gap-3 border-b hairline pb-1.5"
                  >
                    <span className="truncate" title={r.salesman_name}>{r.salesman_name || "—"}</span>
                    <span className="tabular-nums font-medium text-right">{fmtNumber(r.net_value)}</span>
                    <span className="tabular-nums text-myb-500 font-semibold text-right">
                      {r.contribution_pct != null ? `${r.contribution_pct.toFixed(1)}%` : "—"}
                    </span>
                  </div>
                ))}
                {contribData.length > PAGE_SIZE && (
                  <div className="flex items-center justify-between mt-2 text-[11px]">
                    <button
                      data-testid="contrib-prev"
                      disabled={contribPage === 0}
                      onClick={() => setContribPage((p) => Math.max(0, p - 1))}
                      className="px-2 py-1 border rounded-sm hairline disabled:opacity-30 hover:border-myb-500"
                    >‹ Prev</button>
                    <span className="text-muted-foreground tabular-nums">
                      {contribPage * PAGE_SIZE + 1}–{Math.min((contribPage + 1) * PAGE_SIZE, contribData.length)} of {contribData.length}
                    </span>
                    <button
                      data-testid="contrib-next"
                      disabled={(contribPage + 1) * PAGE_SIZE >= contribData.length}
                      onClick={() => setContribPage((p) => p + 1)}
                      className="px-2 py-1 border rounded-sm hairline disabled:opacity-30 hover:border-myb-500"
                    >Next ›</button>
                  </div>
                )}
              </div>
            </div>
          )}
        </Card>
      </Section>

      {/* Brand contribution by location */}
      <Section
        eyebrow="Mix"
        title="Brand contribution by boutique"
        right={
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Boutique</span>
            <SearchSelect
              testId="brand-loc-filter"
              width="w-[260px]"
              height="h-8"
              placeholder="Choose a location…"
              allLabel="— None —"
              value={brandLoc}
              onChange={(v) => setBrandLoc(v)}
              options={locationOptions.map((l) => ({ value: String(l.code), label: l.name || l.code, sub: l.code }))}
            />
          </div>
        }
      >
        <Card testId="brand-contribution-by-location">
          {!brandLoc ? (
            <div className="py-10 text-center text-sm text-muted-foreground italic">Pick a boutique above to see which brand drives what share of its YTD net sales.</div>
          ) : brandLocData.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground italic">No transactions for this boutique in the YTD window.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
              <div className="md:col-span-2">
                <ChartBlock
                  spec={{ kind: "donut", x_key: "brand", y_keys: ["net_value"] }}
                  data={brandLocData}
                  height={320}
                />
              </div>
              <div className="text-xs space-y-1.5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-myb-500 font-semibold mb-2">YTD brand mix · {brandLocData.length} brands</div>
                <div className="grid grid-cols-[minmax(0,1fr)_90px_56px] items-center gap-3 pb-1 border-b hairline text-[9px] uppercase tracking-wider text-muted-foreground/70 font-semibold">
                  <span>Brand</span>
                  <span className="text-right">KWD</span>
                  <span className="text-right">Share</span>
                </div>
                {brandLocData
                  .slice(brandLocPage * PAGE_SIZE, (brandLocPage + 1) * PAGE_SIZE)
                  .map((r, i) => (
                  <div
                    key={r.brand || i}
                    className="grid grid-cols-[minmax(0,1fr)_90px_56px] items-center gap-3 border-b hairline pb-1.5"
                  >
                    <span className="truncate" title={r.brand}>{r.brand || "—"}</span>
                    <span className="tabular-nums font-medium text-right">{fmtNumber(r.net_value)}</span>
                    <span className="tabular-nums text-myb-500 font-semibold text-right">
                      {r.contribution_pct != null ? `${r.contribution_pct.toFixed(1)}%` : "—"}
                    </span>
                  </div>
                ))}
                {brandLocData.length > PAGE_SIZE && (
                  <div className="flex items-center justify-between mt-2 text-[11px]">
                    <button
                      data-testid="brandloc-prev"
                      disabled={brandLocPage === 0}
                      onClick={() => setBrandLocPage((p) => Math.max(0, p - 1))}
                      className="px-2 py-1 border rounded-sm hairline disabled:opacity-30 hover:border-myb-500"
                    >‹ Prev</button>
                    <span className="text-muted-foreground tabular-nums">
                      {brandLocPage * PAGE_SIZE + 1}–{Math.min((brandLocPage + 1) * PAGE_SIZE, brandLocData.length)} of {brandLocData.length}
                    </span>
                    <button
                      data-testid="brandloc-next"
                      disabled={(brandLocPage + 1) * PAGE_SIZE >= brandLocData.length}
                      onClick={() => setBrandLocPage((p) => p + 1)}
                      className="px-2 py-1 border rounded-sm hairline disabled:opacity-30 hover:border-myb-500"
                    >Next ›</button>
                  </div>
                )}
              </div>
            </div>
          )}
        </Card>
      </Section>

      {/* Location contribution per brand — pick a brand, see boutique mix. */}
      <Section
        eyebrow="Mix"
        title="Location contribution per brand"
        right={
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Brand</span>
            <SearchSelect
              testId="locbrand-filter"
              width="w-[260px]"
              height="h-8"
              placeholder="Choose a brand…"
              allLabel="— None —"
              value={locBrand}
              onChange={(v) => setLocBrand(v)}
              options={brandRefList.map((b) => ({ value: b.name, label: b.name }))}
            />
          </div>
        }
      >
        <Card testId="location-contribution-by-brand">
          {!locBrand ? (
            <div className="py-10 text-center text-sm text-muted-foreground italic">Pick a brand above to see which boutiques drive what share of its YTD net sales.</div>
          ) : locBrandData.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground italic">No transactions for this brand in the YTD window.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
              <div className="md:col-span-2">
                <ChartBlock
                  spec={{ kind: "donut", x_key: "location_name", y_keys: ["net_value"] }}
                  data={locBrandData}
                  height={320}
                />
              </div>
              <div className="text-xs space-y-1.5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-myb-500 font-semibold mb-2">YTD location mix · {locBrandData.length} boutiques</div>
                <div className="grid grid-cols-[minmax(0,1fr)_90px_56px] items-center gap-3 pb-1 border-b hairline text-[9px] uppercase tracking-wider text-muted-foreground/70 font-semibold">
                  <span>Boutique</span>
                  <span className="text-right">KWD</span>
                  <span className="text-right">Share</span>
                </div>
                {locBrandData
                  .slice(locBrandPage * PAGE_SIZE, (locBrandPage + 1) * PAGE_SIZE)
                  .map((r, i) => (
                  <div
                    key={r.location_code || i}
                    className="grid grid-cols-[minmax(0,1fr)_90px_56px] items-center gap-3 border-b hairline pb-1.5"
                  >
                    <span className="truncate" title={r.location_name}>{r.location_name || "—"}</span>
                    <span className="tabular-nums font-medium text-right">{fmtNumber(r.net_value)}</span>
                    <span className="tabular-nums text-myb-500 font-semibold text-right">
                      {r.contribution_pct != null ? `${r.contribution_pct.toFixed(1)}%` : "—"}
                    </span>
                  </div>
                ))}
                {locBrandData.length > PAGE_SIZE && (
                  <div className="flex items-center justify-between mt-2 text-[11px]">
                    <button
                      data-testid="locbrand-prev"
                      disabled={locBrandPage === 0}
                      onClick={() => setLocBrandPage((p) => Math.max(0, p - 1))}
                      className="px-2 py-1 border rounded-sm hairline disabled:opacity-30 hover:border-myb-500"
                    >‹ Prev</button>
                    <span className="text-muted-foreground tabular-nums">
                      {locBrandPage * PAGE_SIZE + 1}–{Math.min((locBrandPage + 1) * PAGE_SIZE, locBrandData.length)} of {locBrandData.length}
                    </span>
                    <button
                      data-testid="locbrand-next"
                      disabled={(locBrandPage + 1) * PAGE_SIZE >= locBrandData.length}
                      onClick={() => setLocBrandPage((p) => p + 1)}
                      className="px-2 py-1 border rounded-sm hairline disabled:opacity-30 hover:border-myb-500"
                    >Next ›</button>
                  </div>
                )}
              </div>
            </div>
          )}
        </Card>
      </Section>

      {/* Salesman contribution to a brand — pick a brand, see who sells the most of it. */}
      <Section
        eyebrow="Team"
        title="Salesman contribution per brand"
        right={
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Brand</span>
            <SearchSelect
              testId="salesbrand-filter"
              width="w-[260px]"
              height="h-8"
              placeholder="Choose a brand…"
              allLabel="— None —"
              value={salesBrand}
              onChange={(v) => setSalesBrand(v)}
              options={brandRefList.map((b) => ({ value: b.name, label: b.name }))}
            />
          </div>
        }
      >
        <Card testId="salesman-contribution-by-brand">
          {!salesBrand ? (
            <div className="py-10 text-center text-sm text-muted-foreground italic">Pick a brand above to see which salesmen drive its YTD net sales across the house.</div>
          ) : salesBrandData.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground italic">No transactions for this brand in the YTD window.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
              <div className="md:col-span-2">
                <ChartBlock
                  spec={{ kind: "donut", x_key: "salesman_name", y_keys: ["net_value"] }}
                  data={salesBrandData}
                  height={320}
                />
              </div>
              <div className="text-xs space-y-1.5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-myb-500 font-semibold mb-2">YTD contribution · {salesBrandData.length} salesmen</div>
                <div className="grid grid-cols-[minmax(0,1fr)_90px_56px] items-center gap-3 pb-1 border-b hairline text-[9px] uppercase tracking-wider text-muted-foreground/70 font-semibold">
                  <span>Salesman</span>
                  <span className="text-right">KWD</span>
                  <span className="text-right">Share</span>
                </div>
                {salesBrandData
                  .slice(salesBrandPage * PAGE_SIZE, (salesBrandPage + 1) * PAGE_SIZE)
                  .map((r, i) => (
                  <div
                    key={r.salesman_code || i}
                    className="grid grid-cols-[minmax(0,1fr)_90px_56px] items-center gap-3 border-b hairline pb-1.5"
                  >
                    <span className="truncate" title={r.salesman_name}>{r.salesman_name || "—"}</span>
                    <span className="tabular-nums font-medium text-right">{fmtNumber(r.net_value)}</span>
                    <span className="tabular-nums text-myb-500 font-semibold text-right">
                      {r.contribution_pct != null ? `${r.contribution_pct.toFixed(1)}%` : "—"}
                    </span>
                  </div>
                ))}
                {salesBrandData.length > PAGE_SIZE && (
                  <div className="flex items-center justify-between mt-2 text-[11px]">
                    <button
                      data-testid="salesbrand-prev"
                      disabled={salesBrandPage === 0}
                      onClick={() => setSalesBrandPage((p) => Math.max(0, p - 1))}
                      className="px-2 py-1 border rounded-sm hairline disabled:opacity-30 hover:border-myb-500"
                    >‹ Prev</button>
                    <span className="text-muted-foreground tabular-nums">
                      {salesBrandPage * PAGE_SIZE + 1}–{Math.min((salesBrandPage + 1) * PAGE_SIZE, salesBrandData.length)} of {salesBrandData.length}
                    </span>
                    <button
                      data-testid="salesbrand-next"
                      disabled={(salesBrandPage + 1) * PAGE_SIZE >= salesBrandData.length}
                      onClick={() => setSalesBrandPage((p) => p + 1)}
                      className="px-2 py-1 border rounded-sm hairline disabled:opacity-30 hover:border-myb-500"
                    >Next ›</button>
                  </div>
                )}
              </div>
            </div>
          )}
        </Card>
      </Section>

      {/* First-visit "install to home screen" soft prompt — fixed position,
          dismissible, never re-shown after dismiss or successful install. */}
      <InstallAppPrompt />
    </div>
  );
};

export default Dashboard;
